import { Cesium } from 'app/cesium';
import { MapService } from 'app/map/services/map.service';
import {
    AbstractMapInputHandler,
    IMapInputHandler,
} from 'app/map/services/map_input_handling/MapInputHandler';

export interface ScreenSpaceClickEvent {
    position: Cesium.Cartesian2;
}

export interface ScreenSpaceMovementEvent {
    startPosition: Cesium.Cartesian2;
    endPosition: Cesium.Cartesian2;
}

// Max drag distance that will be interpreted as a click - in scaled Cesium canvas pixels
const MAX_CLICK_DRAG_DISTANCE = 5;

/**
 * Handles user map input when manually setting the location of an Asset on the map
 */
export class AssetMoverMapInputHandler extends AbstractMapInputHandler implements IMapInputHandler {
    private _mapService: MapService;
    private _moveFunction: (location: Cesium.Cartographic) => void;
    private _selectFunction: (location: Cesium.Cartographic) => void;
    private _mouseDownPosition: Cesium.Cartesian2;
    private _dragging: boolean;
    private _cleanUpCallbacks: Array<() => void>;

    constructor(mapService: MapService) {
        super();
        this._mapService = mapService;
        this._moveFunction = undefined;
        this._selectFunction = undefined;
        this._mouseDownPosition = null;
        this._dragging = false;
        this._cleanUpCallbacks = [];
    }

    get scene() {
        return this._mapService.viewer.scene;
    }

    public set moveFunction(moveFunction: (location: Cesium.Cartographic) => void) {
        this._moveFunction = moveFunction.bind(this);
    }

    public set selectFunction(selectFunction: (location: Cesium.Cartographic) => void) {
        this._selectFunction = selectFunction.bind(this);
    }

    private async getCartographicFromCesiumEventPosition(position: Cesium.Cartesian2) {
        const pickRay = this.scene.camera.getPickRay(position);
        const cartesian = this.scene.globe.pick(pickRay, this.scene);
        if (cartesian) {
            return Cesium.Cartographic.fromCartesian(cartesian);
        }
    }

    async leftDown(event: ScreenSpaceClickEvent) {
        this._mouseDownPosition = event.position;
        this._dragging = true;
    }

    async leftUp(event: ScreenSpaceClickEvent) {
        this._dragging = false;
        if (this._mouseDownPosition && event.position) {
            const distance = Cesium.Cartesian2.distance(this._mouseDownPosition, event.position);

            // The check on mouse distance moved is done so that the user can click and drag the screen without
            // selecting a position, but small movements will be ignored
            if (distance < MAX_CLICK_DRAG_DISTANCE) {
                const cartographic = await this.getCartographicFromCesiumEventPosition(
                    event.position,
                );
                if (cartographic) {
                    return this._selectFunction(cartographic);
                }
            }
        }
    }

    async mouseMove(event: ScreenSpaceMovementEvent) {
        if (!this._dragging) {
            const cartographic = await this.getCartographicFromCesiumEventPosition(
                event.endPosition,
            );
            if (cartographic) {
                return this._moveFunction(cartographic);
            }
        }
    }

    public addCleanupCallbacks(fn: () => void): void {
        this._cleanUpCallbacks.push(fn);
    }

    public cleanUp(): void {
        this._cleanUpCallbacks.forEach((fn) => fn());
    }
}
